import type { DriveProfile } from '../../types/drive';

export interface RULResult {
  component: string;
  remainingHours: number;
  percentageUsed: number;
  status: 'ok' | 'warn' | 'critical';
  estimatedEndDate: Date;
}

export class RULCalculator {
  private profile: DriveProfile | null = null;

  setProfile(profile: DriveProfile): void {
    this.profile = profile;
  }

  calculateFanMain(hours: number): RULResult {
    const maxHours = this.profile?.rulParams.fanMainLife ?? 50000;
    return this.calculateLinear('Ventilador Principal', hours, maxHours);
  }

  calculateFanControl(hours: number): RULResult {
    const maxHours = this.profile?.rulParams.fanControlLife ?? 30000;
    return this.calculateLinear('Ventilador de Controle', hours, maxHours);
  }

  calculateCapacitor(
    daysOffline: number,
    rippleLevel: 'normal' | 'high',
    operatingHours: number,
  ): RULResult {
    const baseLife = this.profile?.rulParams.capacitorBaseLife ?? 80000;
    const rippleFactor = rippleLevel === 'high' ? 0.6 : 1.0;
    const offlinePenalty = Math.min(20000, daysOffline * 50);
    const adjustedLife = baseLife * rippleFactor - offlinePenalty;
    const effectiveLife = Math.max(10000, adjustedLife);

    return this.calculateLinear('Capacitores DC Link', operatingHours, effectiveLife);
  }

  calculateIGBT(
    thermalCycles: number,
    maxCycles: number,
    avgTemp: number,
  ): RULResult {
    const maxCyclesAdjusted = this.profile?.rulParams.igbtMaxCycles ?? maxCycles;
    const tempFactor = Math.max(0.3, 1 - (avgTemp - 25) * 0.005);
    const adjustedMax = maxCyclesAdjusted * tempFactor;

    return this.calculateLinear('IGBTs', thermalCycles, Math.max(1000, adjustedMax));
  }

  calculateBearing(
    hours: number,
    loadFactor: number,
    tempFactor: number,
  ): RULResult {
    const baseLife = this.profile?.rulParams.bearingBaseLife ?? 40000;
    const adjustedLife = baseLife * (1 / Math.max(0.5, loadFactor)) * tempFactor;

    return this.calculateLinear('Rolamentos do Motor', hours, Math.max(5000, adjustedLife));
  }

  private calculateLinear(
    component: string,
    current: number,
    max: number,
  ): RULResult {
    const percentageUsed = Math.min(100, (current / max) * 100);
    const remainingHours = Math.max(0, max - current);

    let status: RULResult['status'] = 'ok';
    if (percentageUsed >= 90) status = 'critical';
    else if (percentageUsed >= 75) status = 'warn';

    const estimatedEndDate = new Date(
      Date.now() + remainingHours * 3600 * 1000,
    );

    return {
      component,
      remainingHours: Math.round(remainingHours),
      percentageUsed: Math.round(percentageUsed * 10) / 10,
      status,
      estimatedEndDate,
    };
  }
}
